import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import type { LeadAssignmentRule } from "../types";
import { limitToText, parseLimit } from "./guardiasRules";
import {
  GUARDIAS_KEY,
  HELP_CLASS,
  QueryBoundary,
  SectionCard,
  useGuardiasSave,
  type GuardiasSectionProps,
} from "./guardiasShared";

/**
 * The cap of open unattended leads per advisor and the 'vencido' threshold (hours). Editors
 * only: an asesora neither sees nor requests the rule. An empty value is "Sin límite" (rule off).
 */
export const GuardiasLimits = ({ projectId }: GuardiasSectionProps) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const rules = useQuery({
    queryKey: [GUARDIAS_KEY, projectId, "rule"],
    queryFn: async () =>
      (await dataProvider.getLeadAssignmentRules()).find(
        (rule) => String(rule.project_id) === String(projectId),
      ) ?? null,
  });

  return (
    <SectionCard title={translate("resources.guardias.limits_title")}>
      <QueryBoundary query={rules}>
        {(rule) => <LimitsForm projectId={projectId} rule={rule} />}
      </QueryBoundary>
    </SectionCard>
  );
};

const LimitsForm = ({
  projectId,
  rule,
}: {
  projectId: GuardiasSectionProps["projectId"];
  rule: LeadAssignmentRule | null;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { saving, run } = useGuardiasSave();
  const [cap, setCap] = useState(limitToText(rule?.max_open_unattended_leads));
  const [overdue, setOverdue] = useState(
    limitToText(rule?.overdue_threshold_hours),
  );
  const [invalid, setInvalid] = useState(false);

  const onSave = async () => {
    const parsedCap = parseLimit(cap);
    const parsedOverdue = parseLimit(overdue);
    if (!parsedCap.ok || !parsedOverdue.ok) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    await run(() =>
      dataProvider.setProjectAssignmentLimits(
        projectId,
        parsedCap.value,
        parsedOverdue.value,
      ),
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="guardias-cap">
          {translate("resources.guardias.cap_label")}
        </Label>
        <Input
          id="guardias-cap"
          inputMode="numeric"
          className="h-11 text-base tabular-nums"
          placeholder={translate("resources.guardias.no_limit")}
          value={cap}
          onChange={(e) => setCap(e.target.value)}
        />
        <p className={HELP_CLASS}>{translate("resources.guardias.cap_help")}</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="guardias-overdue">
          {translate("resources.guardias.overdue_label")}
        </Label>
        <Input
          id="guardias-overdue"
          inputMode="numeric"
          className="h-11 text-base tabular-nums"
          placeholder={translate("resources.guardias.no_limit")}
          value={overdue}
          onChange={(e) => setOverdue(e.target.value)}
        />
        <p className={HELP_CLASS}>
          {translate("resources.guardias.overdue_help")}
        </p>
      </div>
      {invalid ? (
        <p role="alert" className="text-[15px] text-destructive">
          {translate("resources.guardias.limit_error")}
        </p>
      ) : null}
      <Button type="button" className="h-11" disabled={saving} onClick={onSave}>
        {translate("resources.guardias.limits_save")}
      </Button>
    </div>
  );
};
